import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box, Container, Card, CardContent, Typography,
    TextField, Button, Alert, CircularProgress, InputAdornment, IconButton
} from '@mui/material';
import { Lock, Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material';
import NavbarMain from '../components/NavbarMain';
import axios from 'axios';
import SEO from '../components/SEO'; // ✅ ДОДАДЕНО

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [tokenValid, setTokenValid] = useState(null);
    const [maskedEmail, setMaskedEmail] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
    const [passwordStrength, setPasswordStrength] = useState({ score: 0, text: '', color: '' });

    useEffect(() => {
        if (!token) {
            setTokenValid(false);
            setLoading(false);
            return;
        }
        validateToken();
    }, [token]);

    const validateToken = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/auth/validate-reset-token?token=${token}`);
            setTokenValid(true);
            setMaskedEmail(res.data.email);
        } catch (err) {
            setTokenValid(false);
            setError(err.response?.data?.message || 'Невалиден или истечен линк');
        } finally {
            setLoading(false);
        }
    };

    const checkPasswordStrength = (password) => {
        let score = 0;
        if (password.length >= 12) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/[0-9]/.test(password)) score++;
        if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score++;
        if (password.length >= 16) score++;

        const levels = [
            { score: 0, text: '', color: '' },
            { score: 1, text: 'Многу слаба', color: '#f44336' },
            { score: 2, text: 'Слаба', color: '#ff7043' },
            { score: 3, text: 'Средна', color: '#ffa726' },
            { score: 4, text: 'Силна', color: '#66bb6a' },
            { score: 5, text: 'Многу силна 💪', color: '#4CAF50' },
        ];
        setPasswordStrength(levels[score] || levels[0]);
    };

    const handlePasswordChange = (e) => {
        const val = e.target.value;
        setFormData({ ...formData, password: val });
        checkPasswordStrength(val);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (formData.password !== formData.confirmPassword) {
            setError('Лозинките не се совпаѓаат');
            return;
        }
        try {
            setSubmitLoading(true);
            await axios.post(`${process.env.REACT_APP_API_URL}/auth/reset-password`, {
                token,
                password: formData.password,
                confirmPassword: formData.confirmPassword
            });
            setSuccess(true);
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Серверска грешка. Обидете се повторно.');
            if (err.response?.data?.expired) {
                setTimeout(() => navigate('/forgot-password'), 2000);
            }
        } finally {
            setSubmitLoading(false);
        }
    };

    const fieldSx = {
        mb: 2,
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            '&:hover fieldset': { borderColor: '#ff7eb9' },
            '&.Mui-focused fieldset': { borderColor: '#ff7eb9' }
        },
        '& .MuiInputLabel-root.Mui-focused': { color: '#ff7eb9' }
    };

    if (loading) {
        return (
            <>
                <NavbarMain />
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                    <CircularProgress sx={{ color: '#ff7eb9' }} />
                </Box>
            </>
        );
    }

    return (
        <>
            {/* ✅ SEO */}
            <SEO
                title="Ресетирај Лозинка - BodyLab by Anamarija"
                description="Ресетирај ја твојата лозинка за BodyLab профилот брзо и безбедно."
                url="/reset-password"
            />

            <NavbarMain />
            <Box sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #ffeef8 0%, #e0f7ff 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                pt: 10, pb: 6, px: 2
            }}>
                <Container maxWidth="sm">
                    <Card sx={{
                        borderRadius: '20px', overflow: 'hidden',
                        boxShadow: '0 8px 32px rgba(255,126,185,0.15)',
                        border: '1px solid rgba(255,126,185,0.2)'
                    }}>
                        {/* Header */}
                        <Box sx={{
                            background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                            p: 4, textAlign: 'center'
                        }}>
                            <Box sx={{
                                width: 64, height: 64, borderRadius: '50%',
                                bgcolor: 'rgba(255,255,255,0.2)',
                                display: 'flex', alignItems: 'center',
                                justifyContent: 'center', mx: 'auto', mb: 2
                            }}>
                                <Lock sx={{ fontSize: 36, color: 'white' }} />
                            </Box>

                            {/* ✅ H1 */}
                            <Typography variant="h1" sx={{
                                color: 'white', fontWeight: 900,
                                fontSize: { xs: '1.6rem', md: '2rem' }
                            }}>
                                Ресетирај Лозинка
                            </Typography>
                        </Box>

                        <CardContent sx={{ p: 4 }}>
                            {/* Невалиден токен */}
                            {!tokenValid && (
                                <Box textAlign="center">
                                    <Typography variant="h2" sx={{ mb: 2 }}>❌</Typography>
                                    <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
                                        {error || 'Невалиден или истечен линк за ресетирање'}
                                    </Alert>
                                    <Button variant="contained"
                                            onClick={() => navigate('/forgot-password')}
                                            sx={{
                                                background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                                borderRadius: '12px'
                                            }}>
                                        🔄 Побарај нов линк
                                    </Button>
                                </Box>
                            )}

                            {/* Успешен reset */}
                            {success && (
                                <Box textAlign="center">
                                    <CheckCircle sx={{ fontSize: 80, color: '#4CAF50', mb: 2 }} />
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: '#4CAF50' }}>
                                        Лозинката е успешно ресетирана!
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#888', mb: 3 }}>
                                        Ќе бидете пренасочени кон логирање за 3 секунди...
                                    </Typography>
                                    <CircularProgress sx={{ color: '#4CAF50' }} />
                                </Box>
                            )}

                            {/* Форма */}
                            {tokenValid && !success && (
                                <form onSubmit={handleSubmit}>
                                    {maskedEmail && (
                                        <Alert severity="info" sx={{ mb: 3, borderRadius: '12px' }}>
                                            Ресетирање на лозинка за: <strong>{maskedEmail}</strong>
                                        </Alert>
                                    )}
                                    {error && (
                                        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
                                            {error}
                                        </Alert>
                                    )}

                                    <TextField
                                        fullWidth label="Нова лозинка"
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.password}
                                        onChange={handlePasswordChange}
                                        sx={fieldSx}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />

                                    {formData.password && (
                                        <Box sx={{ mb: 2 }}>
                                            <Box sx={{
                                                height: 6, borderRadius: 3,
                                                bgcolor: '#eee', overflow: 'hidden', mb: 1
                                            }}>
                                                <Box sx={{
                                                    height: '100%',
                                                    width: `${(passwordStrength.score / 5) * 100}%`,
                                                    bgcolor: passwordStrength.color,
                                                    borderRadius: 3, transition: 'all 0.3s ease'
                                                }} />
                                            </Box>
                                            <Typography variant="caption" sx={{ color: passwordStrength.color, fontWeight: 'bold' }}>
                                                {passwordStrength.text}
                                            </Typography>
                                        </Box>
                                    )}

                                    <TextField
                                        fullWidth label="Потврди нова лозинка"
                                        type={showConfirm ? 'text' : 'password'}
                                        value={formData.confirmPassword}
                                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                        sx={fieldSx}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowConfirm(!showConfirm)}>
                                                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />

                                    <Box sx={{
                                        bgcolor: 'rgba(255,126,185,0.05)',
                                        border: '1px solid rgba(255,126,185,0.2)',
                                        borderRadius: '12px', p: 2, mb: 3
                                    }}>
                                        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1, fontWeight: 'bold' }}>
                                            Барања за лозинка:
                                        </Typography>
                                        {[
                                            { check: formData.password.length >= 12, text: 'Минимум 12 карактери' },
                                            { check: /[A-Z]/.test(formData.password), text: 'Барем 1 голема буква' },
                                            { check: /[0-9]/.test(formData.password), text: 'Барем 1 број' },
                                            { check: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password), text: 'Барем 1 специјален знак' },
                                        ].map((req, i) => (
                                            <Typography key={i} variant="caption" sx={{
                                                display: 'block', color: req.check ? '#4CAF50' : '#bbb'
                                            }}>
                                                {req.check ? '✅' : '⬜'} {req.text}
                                            </Typography>
                                        ))}
                                    </Box>

                                    <Button fullWidth type="submit" variant="contained"
                                            disabled={submitLoading}
                                            sx={{
                                                background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                                borderRadius: '12px', py: 1.5,
                                                fontSize: '1rem', fontWeight: 'bold',
                                                '&:hover': { background: 'linear-gradient(45deg, #ff4a97, #9C27B0)' }
                                            }}>
                                        {submitLoading ? <CircularProgress size={24} color="inherit" /> : '🔐 Ресетирај лозинка'}
                                    </Button>
                                </form>
                            )}
                        </CardContent>
                    </Card>
                </Container>
            </Box>
        </>
    );
};

export default ResetPassword;